import type { VibeClaimResult } from './vibeClaimApi';

// Pure decision table: what a claim result / error means for the pending
// claim and for the user. Kept separate from the coordinator so every branch
// is unit-tested (see __tests__/vibeClaimOutcome.test.ts).

export type VibeClaimNotice =
  | { kind: 'added'; resultsReady: boolean }
  | { kind: 'already_completed' }
  | { kind: 'round_closed'; dateKey: string }
  | { kind: 'claimed_elsewhere' }
  | { kind: 'not_found' }
  | { kind: 'not_eligible' };

export type VibeClaimDecision = {
  /** true → the server gave a final answer; drop the pending claim. */
  clearPending: boolean;
  /** Where to take the user, if anywhere. */
  navigateTo: 'DayVibesAura' | null;
  notice: VibeClaimNotice | null;
};

export function decideFromResult(result: VibeClaimResult): VibeClaimDecision {
  switch (result.outcome) {
    case 'submitted':
      return { clearPending: true, navigateTo: 'DayVibesAura', notice: { kind: 'added', resultsReady: result.resultsReady } };
    case 'already_completed':
      return { clearPending: true, navigateTo: null, notice: { kind: 'already_completed' } };
    case 'round_closed':
      return { clearPending: true, navigateTo: null, notice: { kind: 'round_closed', dateKey: result.dateKey } };
    default:
      return { clearPending: true, navigateTo: null, notice: { kind: 'not_found' } };
  }
}

const FINAL_REASONS: Record<string, VibeClaimNotice> = {
  claimed_elsewhere: { kind: 'claimed_elsewhere' },
  not_found: { kind: 'not_found' },
  invalid_code: { kind: 'not_found' },
  invalid_session: { kind: 'not_found' },
  not_eligible: { kind: 'not_eligible' },
};

/**
 * Errors with a final reason clear the claim and explain why. Everything else
 * (offline, server hiccup, rate limit, profile/email not ready yet, signed
 * out) is transient: keep the claim and retry later, silently.
 */
export function decideFromError(error: unknown): VibeClaimDecision {
  const reason = (error as { reason?: unknown } | null)?.reason;
  if (typeof reason === 'string' && FINAL_REASONS[reason]) {
    return { clearPending: true, navigateTo: null, notice: FINAL_REASONS[reason] };
  }
  return { clearPending: false, navigateTo: null, notice: null };
}
